/**
 * The AI opponent, as far as the *wire* is concerned.
 *
 * The owner's own words: "Play against an AI (with different difficulties), in
 * different game modes." What crosses the wire for that is remarkably little,
 * and the smallness is the design rather than an accident:
 *
 * - **A difficulty level**, chosen at room creation (`POST /api/rooms`) and
 *   echoed back, so a lobby can offer four of them and a server can refuse
 *   anything else.
 * - **A marker on the seat** (`PlayerView.bot`), so a screen never has to
 *   guess from a display name whether it is looking at a person.
 *
 * Everything else — the candidate search, the reaction delay, the misdrop
 * probability, the aggression that decides whether a clear is worth taking —
 * lives in `apps/server/src/bot/` and never leaves the server. The bot has no
 * socket and no client: it is a driver inside the room that produces *ordinary
 * input events* for its seat, which the server validates, replays and relays
 * exactly as it does a human's. So there is nothing here about how it plays,
 * because no peer needs to know: a mirror of the bot's board is rebuilt from
 * the same relayed inputs a human's board is, through the same `SimDriver`,
 * and the hash pipeline checks it the same way.
 *
 * That is also why `PROTOCOL_VERSION` did not move for this batch — see
 * `version.ts`, which states the reasoning where the constant lives.
 */

/**
 * How hard the AI plays. Four levels, and the names are the ones the lobby
 * shows: a difficulty a player cannot name is a difficulty they cannot choose
 * between.
 *
 * Deliberately *not* `@interlock/modes`' `Hardness`. Hardness scales the
 * gravity both seats fall under — it is a property of the *board* — and this
 * scales how well one seat plays it. A room can be Expert hardness with an
 * Easy opponent, and that combination is a perfectly sensible thing to want.
 */
export type AiLevel = 'easy' | 'normal' | 'hard' | 'expert';

/** Spec order — easiest first, which is the order the picker offers them in. */
export const AI_LEVELS: readonly AiLevel[] = ['easy', 'normal', 'hard', 'expert'];

/**
 * The level an unconfigured "play vs AI" request gets.
 *
 * `normal` for the same reason `@interlock/modes`' `DEFAULT_HARDNESS` is: the
 * middle of a four-point scale is the only choice that is not an opinion about
 * who is asking.
 */
export const DEFAULT_AI_LEVEL: AiLevel = 'normal';

export function isAiLevel(value: unknown): value is AiLevel {
  return typeof value === 'string' && (AI_LEVELS as readonly string[]).includes(value);
}

const AI_LEVEL_NAME: Readonly<Record<AiLevel, string>> = {
  easy: 'Easy',
  normal: 'Normal',
  hard: 'Hard',
  expert: 'Expert',
};

export function aiLevelName(level: AiLevel): string {
  return AI_LEVEL_NAME[level];
}

/**
 * The display name the bot's seat plays under: `AI — Hard`.
 *
 * **Honest by construction.** The server sets this as the seat's `name`, so
 * every surface that already renders a seat name — the duel HUD, the lobby
 * roster, the pause overlay, `match_participant.display_name` in the database,
 * the admin panel's match history — says "AI" without one of them having to be
 * taught about bots. `PlayerView.bot` is the machine-readable half for the
 * screens that want a badge as well; this is the half that cannot be missed.
 *
 * Inside the 16-character display-name limit at every level, so it survives
 * the same sanitiser a guest name goes through. **A person can type it too**:
 * nothing stops a guest from calling themselves `AI — Hard`, which is exactly
 * why the *marker* (`PlayerView.bot`), not the name, is what a screen
 * branches on.
 */
export function aiSeatName(level: AiLevel): string {
  return `AI — ${AI_LEVEL_NAME[level]}`;
}
